import type { FilterOptions } from '../product-listing-filter-sheet';
import type { FilterValuesType } from '../use-product-listing-filters';
import type {
  ProductListingPageBaseProps,
  ProductListingPageSettings,
} from './product-listing-types';

export function buildSmartFilter(
  conditions: ProductListingPageSettings['conditions'],
  matchType: ProductListingPageSettings['conditionsMatchType']
): string {
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
  const parts = conditions?.map((c) => {
    switch (c._type) {
      // # String filters
      case 'productTitleCondition': {
        if (c.operation === 'contains')
          return `lower(title) match "*${c.value.toLowerCase()}*"`;
        if (c.operation === 'equals')
          return `lower(title) == "${c.value.toLowerCase()}"`;
        if (c.operation === 'notContains')
          return `!(lower(title) match "*${c.value.toLowerCase()}*")`;
        if (c.operation === 'notEquals')
          return `lower(title) != "${c.value.toLowerCase()}"`;
        return 'false';
      }
      case 'category': {
        if (c.operation === 'contains') return `category match "*${c.value}*"`;
        if (c.operation === 'equals') return `category == "${c.value}"`;
        if (c.operation === 'notContains')
          return `category !match "*${c.value}*"`;
        if (c.operation === 'notEquals') return `category != "${c.value}"`;
        return 'false';
      }
      // # Price filters
      case 'productPriceCondition': {
        if (c.operation === 'equals') return `price == "${c.value}"`;
        if (c.operation === 'notEquals') return `price != "${c.value}"`;
        if (c.operation === 'greaterThan') return `price > "${c.value}"`;
        if (c.operation === 'lessThan') return `price < "${c.value}"`;
        return 'false';
      }
      // # Reference filters
      case 'finishReference': {
        if (c.operation === 'is') return `finish._ref == "${c.value._ref}"`;
        if (c.operation === 'isNot') return `finish._ref != "${c.value._ref}"`;
        return 'false';
      }
      case 'materialReference': {
        if (c.operation === 'is') return `material._ref == "${c.value._ref}"`;
        if (c.operation === 'isNot')
          return `material._ref != "${c.value._ref}"`;
        return 'false';
      }
      case 'productFamilyReference': {
        if (c.operation === 'is')
          return `productFamily._ref == "${c.value._ref}"`;
        if (c.operation === 'isNot')
          return `productFamily._ref != "${c.value._ref}"`;
        return 'false';
      }
      case 'shapeReference': {
        if (c.operation === 'is') return `shape._ref == "${c.value._ref}"`;
        if (c.operation === 'isNot') return `shape._ref != "${c.value._ref}"`;
        return 'false';
      }
      case 'productColorReference': {
        if (c.operation === 'is') return `color._ref == "${c.value._ref}"`;
        if (c.operation === 'isNot') return `color._ref != "${c.value._ref}"`;
        return 'false';
      }
      case 'sizeReference': {
        if (c.operation === 'is') return `size._ref == "${c.value._ref}"`;
        if (c.operation === 'isNot') return `size._ref != "${c.value._ref}"`;
        return 'false';
      }
      case 'patternReference': {
        if (c.operation === 'is') return `pattern._ref == "${c.value._ref}"`;
        if (c.operation === 'isNot') return `pattern._ref != "${c.value._ref}"`;
        return 'false';
      }
      case 'brandReference': {
        if (c.operation === 'is') return `brand._ref == "${c.value._ref}"`;
        if (c.operation === 'isNot') return `brand._ref != "${c.value._ref}"`;
        return 'false';
      }
      // # Array reference filters
      case 'roomReference': {
        if (c.operation === 'is') return `"${c.value._ref}" in rooms[]._ref`;
        if (c.operation === 'isNot')
          return `"${c.value._ref}" not in rooms[]._ref`;
        return 'false';
      }
      default: {
        const _exhaustiveCheck: never = c;
        return _exhaustiveCheck;
      }
    }
  });

  const joiner = matchType === 'all' ? ' && ' : ' || ';
  return parts?.join(joiner) || 'true';
}

export function buildUserFilters(filters?: FilterValuesType): string | null {
  if (!filters) return null;

  const conditions: string[] = [];

  // Handle color filters - need to look up color IDs by slug
  if (
    filters.color &&
    Array.isArray(filters.color) &&
    filters.color.length > 0
  ) {
    const colorSlugs = filters.color as string[];
    conditions.push(
      `color._ref in *[_type == "productColor" && slug.current in [${colorSlugs.map((s) => `"${s}"`).join(', ')}]]._id`
    );
  }

  // Handle finish filters - need to look up finish IDs by slug
  if (
    filters.finish &&
    Array.isArray(filters.finish) &&
    filters.finish.length > 0
  ) {
    const finishSlugs = filters.finish as string[];
    conditions.push(
      `finish._ref in *[_type == "finish" && slug.current in [${finishSlugs.map((s) => `"${s}"`).join(', ')}]]._id`
    );
  }

  // Handle price range filters
  if (filters.priceMin && filters.priceMin !== '') {
    conditions.push(`price >= ${filters.priceMin}`);
  }
  if (filters.priceMax && filters.priceMax !== '') {
    conditions.push(`price <= ${filters.priceMax}`);
  }

  return conditions.length > 0 ? conditions.join(' && ') : null;
}

export function buildFilterOptions(
  data: ProductListingPageBaseProps
): FilterOptions[] {
  const filterOptions: FilterOptions[] = [];

  if (!data.filterOptions) {
    return filterOptions;
  }

  if (
    data.filterOptions.priceRange?.min != null &&
    data.filterOptions.priceRange?.max != null
  ) {
    filterOptions.push({
      type: 'price' as const,
      priceRange: {
        min: data.filterOptions.priceRange.min,
        max: data.filterOptions.priceRange.max,
      },
    });
  }

  if (data.filterOptions.colors && data.filterOptions.colors.length > 0) {
    filterOptions.push({
      type: 'color' as const,
      options: data.filterOptions.colors,
    });
  }

  if (data.filterOptions.finishes && data.filterOptions.finishes.length > 0) {
    filterOptions.push({
      type: 'finish' as const,
      options: data.filterOptions.finishes,
    });
  }

  return filterOptions;
}

export function buildInitialFilterValues(
  filterOptions: FilterOptions[]
): FilterValuesType {
  const initialValues: FilterValuesType = {};

  for (const option of filterOptions) {
    if (option.type === 'price') {
      initialValues.priceMin = '';
      initialValues.priceMax = '';
    } else if (
      option.type === 'color' ||
      option.type === 'finish' ||
      option.type === 'pattern'
    ) {
      initialValues[option.type] = [];
    }
  }

  return initialValues;
}
